// The run-panel calculations, ported from the coffee DataRunView. The panel offers to crunch
// the numbers for a report whose data is incomplete, and reports the job's progress while it
// runs; the crunching itself stays on the page side, behind onRun.

export const JOB_STATUS = { disabled: 'disabled', idle: 'idle', running: 'running', failed: 'failed' }

// A guest cannot start a run, so an idle job reads as unavailable to them. A block that exists
// only to offer the run says so with `defaultJobStatus` -- there is nothing to disable, since
// there is nothing else on screen.
export const resolveJobState = ({ answer, isGuest = false, defaultJobStatus = JOB_STATUS.disabled } = {}) => {
  const status = answer?.jobStatus || defaultJobStatus

  if (isGuest && JOB_STATUS.idle === status) {
    return { jobStatus: JOB_STATUS.disabled, jobProgress: 0, jobEta: 0 }
  }

  return { jobStatus: status, jobProgress: answer?.jobProgress || 0, jobEta: answer?.jobEta || 0 }
}

// The progress bar is driven by a total/current pair rather than a percentage: the eta stretches
// the total so the bar keeps moving at the rate the job is actually reporting.
const etaFactor = (jobProgress, jobEta) => (100 > jobProgress ? (0 < jobEta ? jobEta / (100 - jobProgress) : 6) : 1)

export const countTotalTime = ({ jobProgress = 0, jobEta = 0 } = {}) => 100000 * etaFactor(jobProgress, jobEta)

export const countCurrentTime = ({ jobProgress = 0, jobEta = 0 } = {}) =>
  1000 * jobProgress * etaFactor(jobProgress, jobEta)

// The remaining-time text is switched off (the coffee returned an empty string unconditionally);
// the minutes calculation is kept so it can be turned back on.
const SHOW_REMAIN_TIME = false

export const formatRemainTime = ({ jobEta } = {}) => {
  const minutes = jobEta && Number.isFinite(jobEta) ? Math.ceil(jobEta / 60) : 15

  return SHOW_REMAIN_TIME ? `~ ${minutes} Minutes left` : ''
}

// A report that already has data offers to re-run; one that never ran offers to run.
export const calcRunLabel = ({ badData, answer, isRunning, jobEta } = {}) => {
  if (isRunning) return `RUNNING REPORT ${formatRemainTime({ jobEta })}`.trim()

  const rerun = !badData && ((answer?.difference || 0) > 5 || false === answer?.actualData)

  return `${rerun ? 'RE-' : ''}RUN THE REPORT (5-15 min)`
}

const PANELS = {
  idle: {
    title: "CAN'T WAIT?",
    message: 'While survey is still running you can crunch the numbers that we collected so far'
  },
  running: {
    title: 'CRUNCHING NUMBERS',
    message: "Please wait while we're doing some really advanced data crunching for you"
  }
}

// What the panel says, and whether its loader is frozen: a job in flight animates, an offer to
// run does not.
export const calcRunPanel = ({ jobStatus, note = '', isRunning = false } = {}) => {
  if (isRunning) return { ...PANELS.running, freezed: false }
  if (JOB_STATUS.disabled === jobStatus) return { title: 'NOT ENOUGH DATA', message: note, freezed: true }
  if ([JOB_STATUS.idle, JOB_STATUS.failed].includes(jobStatus)) return { ...PANELS.idle, freezed: true }

  return null
}

// The panel only appears for a question whose data is missing or stale.
export const isRunOffered = ({ badData, answer } = {}) =>
  Boolean(badData || answer?.difference || false === answer?.actualData || answer?.insufficientSample)
